<template>
  <!-- Лента дней над вкладкой «неделя»: квадраты того же размера и с тем же
       зазором, что календарь на «графиках» — семь в ряд, (ширина − 6 × 6) / 7
       (владелец 28.10.2026). Лента листается: назад — к первому дню дневника,
       вперёд — к запланированным дням. Открывается на последних семи днях по
       сегодняшний. Выбранный день обведён, сегодня отмечено подписью;
       неполные дни показаны штриховкой. Цвета дней за пределами недели родитель догружает по
       месяцам, когда лента докручена до них (событие reach). -->
  <div ref="rootEl" class="week-picker px-3 pt-2 pb-1">
    <div ref="scrollEl" class="strip" :style="{ gap: GAP + 'px', '--fade-left': fadeLeft ? '16px' : '0px', '--fade-right': fadeRight ? '16px' : '0px' }" @scroll.passive="onScroll">
      <button
        v-for="day in days"
        :key="day.date"
        :data-date="day.date"
        @click="$emit('select', day.date)"
        class="flex flex-col items-center gap-0.5 rounded-xl transition-colors shrink-0 snap-start"
        :class="selectedDate === day.date ? 'day-selected' : ''"
        :style="{ width: cell + 'px' }"
      >
        <span class="text-13 text-ink/70" style="font-family: Inter, sans-serif;">{{ day.weekday }}</span>
        <span
          class="flex items-center justify-center rounded-xl text-18 text-ink"
          :class="[[day.isToday ? 'font-bold' : 'font-light', selectedDate === day.date ? 'day-selected-date' : ''], day.isPlanned ? 'day-planned' : '', day.isFuture && !day.isPlanned ? 'opacity-30' : '']"
          :style="{ fontFamily: 'Inter, sans-serif', width: cell + 'px', height: cell + 'px', ...(day.isFuture ? {} : bgStyle(day.date, day.hasEntries)) }"
        >
          {{ day.dayNum }}
        </span>
        <span v-if="day.isToday" class="today-label">сегодня</span>
        <span v-else class="today-label" aria-hidden="true">&nbsp;</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { dayBackground } from '../utils/kcalDayColor.js'
import { addDays } from '../utils/periodCharts.js'

const props = defineProps({
  dayStates: { type: Object, default: () => ({}) },
  daysWithEntries: { type: Array, default: () => [] },
  selectedDate: { type: String, default: null },
  dayKcals: { type: Object, default: () => ({}) },
  kcalNorm: { type: Number, default: 0 },
  showKcalColors: { type: Boolean, default: true },
  // Будущие дни с записями (GET /diary/summary, planned_days): человек
  // повторил блюдо «на завтра / на неделю» и хочет проверить, что записалось
  plannedDays: { type: Array, default: () => [] },
  // Первый день дневника — докуда можно листать назад (без него — 30 дней)
  firstDay: { type: String, default: null },
})

const emit = defineEmits(['select', 'reach'])

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']
const GAP = 6
const VISIBLE = 7

function toLocalDateStr(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
const todayStr = toLocalDateStr(new Date())

// ── Размер квадрата — как в MonthCalendar: (ширина − 6 зазоров) / 7 ──
const rootEl = ref(null)
defineExpose({ rootEl })
const scrollEl = ref(null)
const cell = ref(46)
const fadeLeft = ref(false)
const fadeRight = ref(false)
function updateFades() {
  const el = scrollEl.value
  if (!el) return
  fadeLeft.value = el.scrollLeft > 1
  fadeRight.value = el.scrollWidth - el.clientWidth - el.scrollLeft > 1
}
let ro = null
let positioned = false
function measure() {
  if (!scrollEl.value) return
  cell.value = Math.floor((scrollEl.value.clientWidth - (VISIBLE - 1) * GAP) / VISIBLE)
}
onMounted(async () => {
  measure()
  if (typeof ResizeObserver !== 'undefined' && scrollEl.value) { ro = new ResizeObserver(async () => { measure(); await nextTick(); if (positioned) scrollToToday(false) }); ro.observe(scrollEl.value) }
  await nextTick()
  scrollToToday(false)
  positioned = true
})
onUnmounted(() => { ro?.disconnect(); clearTimeout(reachTimer) })

function bgStyle(dateStr, hasEntries) {
  return dayBackground(dateStr, hasEntries, { dayStates: props.dayStates, showKcalColors: props.showKcalColors, kcalNorm: props.kcalNorm, dayKcals: props.dayKcals })
}

// Дни ленты: от первого дня дневника (не дальше года назад) по сегодня,
// затем запланированные дни впереди, не дальше недели
const days = computed(() => {
  const entries = new Set(props.daysWithEntries)
  const minStart = addDays(todayStr, -365)
  let start = props.firstDay && props.firstDay > minStart ? props.firstDay : minStart
  if (!props.firstDay) start = addDays(todayStr, -30)
  if (start > addDays(todayStr, -6)) start = addDays(todayStr, -6)
  const out = []
  for (let d = start; d <= todayStr; d = addDays(d, 1)) out.push(dayOf(d, entries))
  const limit = addDays(todayStr, 7)
  for (const d of [...props.plannedDays].filter(x => x > todayStr && x <= limit).sort()) out.push({ ...dayOf(d, entries), isPlanned: true })
  return out
})
function dayOf(dateStr, entries) {
  const d = new Date(dateStr + 'T12:00:00')
  const jsDay = d.getDay()
  return {
    date: dateStr,
    dayNum: d.getDate(),
    weekday: WEEKDAYS[jsDay === 0 ? 6 : jsDay - 1],
    isToday: dateStr === todayStr,
    isFuture: dateStr > todayStr,
    isPlanned: false,
    hasEntries: entries.has(dateStr),
  }
}

// Открыть на последних семи днях по сегодняшний
function scrollToToday(smooth) {
  const el = scrollEl.value
  if (!el) return
  const idx = days.value.findIndex(d => d.date === todayStr)
  if (idx < 0) return
  const left = Math.max(0, (idx - (VISIBLE - 1)) * (cell.value + GAP))
  el.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' })
  updateFades()
}
// Лента стала длиннее (пришёл первый день дневника) — не сдвигать то, что человек видит
watch(() => days.value.length, async (n, was) => {
  if (!positioned || !scrollEl.value) return
  await nextTick()
  const added = n - was
  if (added > 0 && days.value[0].date < (days.value[added]?.date || '')) {
    scrollEl.value.scrollLeft += added * (cell.value + GAP)
  }
  updateFades()
})

// Докрутили — сообщаем самый левый видимый день, родитель догружает его месяц
let reachTimer = null
function onScroll() {
  updateFades()
  clearTimeout(reachTimer)
  reachTimer = setTimeout(() => {
    const el = scrollEl.value
    if (!el) return
    const idx = Math.max(0, Math.floor(el.scrollLeft / (cell.value + GAP)))
    const d = days.value[idx]
    if (d) emit('reach', d.date)
  }, 120)
}
</script>

<style scoped>
/* Плашка с датами недели — полупрозрачная с размытием, как нижнее меню.
   Тон взят у активного таба (rgba(250, 243, 232, 0.75)), а не у панели с
   неактивными: лента продолжает вкладку «неделя» и должна сливаться с ней. */
.week-picker {
  touch-action: pan-x pinch-zoom;
  background: rgba(250, 243, 232, 0.75);
  -webkit-backdrop-filter: blur(20px);
  backdrop-filter: blur(20px);
}
.strip {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  touch-action: pan-x pinch-zoom;
  scroll-snap-type: x mandatory;
  overscroll-behavior: none;
  -webkit-mask-image: linear-gradient(to right, transparent, #000 var(--fade-left), #000 calc(100% - var(--fade-right)), transparent);
  mask-image: linear-gradient(to right, transparent, #000 var(--fade-left), #000 calc(100% - var(--fade-right)), transparent);
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.strip::-webkit-scrollbar { display: none; }
.snap-start { scroll-snap-align: start; }
/* Выбранный день: плашка светлее фона, кружок с цветом дня читается вложенным в неё */
.day-selected { background: transparent; }
.day-selected-date { box-shadow: inset 0 0 0 1.5px var(--ink-70); }
.today-label { font-family: Inter,sans-serif; font-size: calc(var(--fs-13) * .8); color: var(--ink-70); line-height:1.2; }
.day-planned { outline: 1.5px dashed rgba(65, 41, 26, 0.55); outline-offset: -1.5px; }
</style>
